import { LitElement, html, css, type TemplateResult, type PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { getFirebaseAuth } from '../lib/firebase';
import { setPageTitle } from '../lib/page-title';
import { startInkCanvas } from '../lib/ink-canvas';

/**
 * OPEN magic-link invite landing — `/accept?token=<token>`.
 *
 * Admins now invite new users by emailing a magic link of the form
 * `https://decks.rdx.tools/accept?token=<token>`. Clicking it lands here.
 * The invitee is NOT signed in — the opaque token in the URL is the
 * credential. This page lets them SET THEIR PASSWORD and signs them straight
 * in (their first login); there is no separate "create account" step.
 *
 * The flow is a single POST over the OPEN backend endpoint (same-origin, no
 * Authorization header — the token is the proof of invite):
 *
 *   POST /api/auth/invite/accept { token, password, name? }
 *     200 { customToken } — exchange via Firebase signInWithCustomToken, then
 *                           FULL reload to "/" so the shell gate re-evaluates
 *                           to 'authed' and the invitee lands inside the app.
 *     401 { error }       — invalid / expired link (surface a friendly fixed
 *                           message rather than the raw server text).
 *     422 { error }       — weak password (surfaced VERBATIM so the invitee
 *                           sees the exact policy requirement).
 *     400 { error }       — other bad request (surfaced verbatim).
 *
 * The chrome (ink-canvas background, centred panel, WORKPLACE BY RICOH
 * eyebrow, Fraunces heading, field/button styling) deliberately mirrors
 * login-page so the first-login experience is visually identical.
 *
 * When the URL carries no token we render an "invalid invite link" panel with
 * no form — there is nothing to submit without the credential.
 */

/** Shape of the OPEN /api/auth/invite/accept response (200). */
interface InviteAcceptResponse {
  customToken?: string;
}

@customElement('accept-page')
export class AcceptPage extends LitElement {
  static styles = css`
    :host {
      display: flex;
      min-height: 100vh;
      align-items: center;
      justify-content: center;
      background: var(--canvas);
      padding: 32px;
      position: relative;
      overflow: hidden;
    }
    canvas.ink {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
      pointer-events: none;
    }
    .panel {
      position: relative;
      z-index: 1;
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius, 12px);
      padding: 40px;
      max-width: 420px;
      width: 100%;
      box-shadow: var(--shadow-overlay);
    }
    .eyebrow {
      font-family: var(--font-sans), 'Inter', system-ui, sans-serif;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-tertiary);
      margin: 0 0 8px;
    }
    h1 {
      font-family: var(--font-editorial), 'Fraunces', Georgia, serif;
      font-weight: 400;
      font-size: 1.9rem;
      letter-spacing: -0.01em;
      margin: 0 0 8px;
      color: var(--text-primary);
    }
    .subtitle {
      color: var(--text-secondary);
      margin: 0 0 28px;
      font-size: 0.95rem;
      line-height: 1.5;
    }
    form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    label {
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      color: var(--text-secondary);
    }
    input {
      width: 100%;
      font: inherit;
      font-size: 0.95rem;
      color: var(--text-primary);
      background: var(--canvas);
      border: 1px solid var(--border-default, var(--border-subtle));
      border-radius: var(--radius-sm, 8px);
      padding: 11px 13px;
      transition: border-color 120ms, box-shadow 120ms;
    }
    input::placeholder {
      color: var(--text-tertiary);
    }
    input:focus {
      outline: none;
      border-color: var(--ink);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--ink) 18%, transparent);
    }
    input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    button.primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 44px;
      padding: 0 18px;
      /* The primary CTA is the BRAND ink — the accent the theme-boot writes to
         --brand-primary (RDx green by default; the per-user ink picker
         overrides it). NOT the Paper --ink token: that is a FOREGROUND token
         that inverts to a pale blue-grey on dark themes, which made the CTA
         look disabled. --brand-on-accent is the WCAG-correct on-colour. */
      background: var(--brand-primary, var(--ink));
      color: var(--brand-on-accent, var(--on-ink));
      border: 1px solid var(--brand-primary, var(--ink));
      border-radius: var(--radius-sm, 8px);
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 120ms, background 120ms, box-shadow 160ms, transform 120ms;
    }
    button.primary:hover:not(:disabled) {
      background: var(--brand-primary-hover, var(--brand-primary, var(--ink-hover, var(--ink))));
      transform: translateY(-1px);
      box-shadow: 0 6px 18px -6px color-mix(in srgb, var(--brand-primary, var(--ink)) 55%, transparent);
    }
    button.primary:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: none;
    }
    button.primary:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary, var(--ink)) 35%, transparent);
    }
    button.primary:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .error {
      background: var(--error-soft);
      border: 1px solid var(--error-strong);
      color: var(--text-primary);
      padding: 12px 14px;
      border-radius: var(--radius-sm, 8px);
      font-size: 0.88rem;
      line-height: 1.45;
      margin: 0;
    }
    .notice {
      color: var(--text-secondary);
      font-size: 0.95rem;
      line-height: 1.6;
      margin: 0;
    }
  `;

  /** The opaque invite token read from `?token=` on the magic link. */
  @state() private token = '';

  /** True while the accept request is in flight; disables inputs/buttons. */
  @state() private busy = false;

  /** User-facing error message, shown in the aria-live region. */
  @state() private error: string | null = null;

  /** True once we've redirected (custom token exchanged) — calm "signing in". */
  @state() private done = false;

  /** The password the invitee is choosing. */
  @state() private password = '';

  /** Optional display name. */
  @state() private name = '';

  /** Autofocus target (the password field) once the form is in the DOM. */
  @query('[data-autofocus]') private autofocusEl?: HTMLInputElement;

  /** Tracks whether we've already moved focus, so we only do it once. */
  private focused = false;

  /** The flowing-ink background canvas + its teardown handle. */
  @query('canvas.ink') private inkCanvas?: HTMLCanvasElement;
  private stopInk?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Set your password');
    // The token is the credential — read it once from the magic link's query.
    this.token = (new URLSearchParams(location.search).get('token') ?? '').trim();
  }

  firstUpdated(): void {
    if (this.inkCanvas) this.stopInk = startInkCanvas(this.inkCanvas, this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopInk?.();
    this.stopInk = undefined;
  }

  protected updated(_changed: PropertyValues): void {
    // Move focus to the password field once, after the form first renders.
    if (!this.focused && this.token && !this.done) {
      this.focused = true;
      queueMicrotask(() => this.autofocusEl?.focus());
    }
  }

  // ─── Exchange the custom token + redirect ────────────────────────────
  // On success the server returns a Firebase custom token. Exchange it via
  // signInWithCustomToken to mint a real session, then FULL reload to "/" so
  // the shell's auth gate re-runs and resolves to 'authed' — the invitee lands
  // inside the app on their first login. (Mirrors login-page.activateAndRedirect.)
  private async activateAndRedirect(customToken: string): Promise<void> {
    const auth = await getFirebaseAuth();
    if (!auth) {
      this.error = 'Sign-in is unavailable right now. Please reload and try again.';
      this.busy = false;
      return;
    }
    const { signInWithCustomToken } = await import('firebase/auth');
    await signInWithCustomToken(auth, customToken);
    this.done = true;
    // Full reload — the shell's auth gate re-runs and resolves to 'authed'.
    window.location.assign('/');
  }

  // ─── Submit: set password + accept the invite ────────────────────────
  private onSubmit = async (ev: Event): Promise<void> => {
    ev.preventDefault();
    if (!this.token || !this.password || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      const body: { token: string; password: string; name?: string } = {
        token: this.token,
        password: this.password,
      };
      const trimmedName = this.name.trim();
      if (trimmedName) body.name = trimmedName;

      const res = await fetch('/api/auth/invite/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      let payload: unknown;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }

      if (!res.ok) {
        // 401: invalid/expired link — a fixed, actionable message (the raw
        // server text here is not invitee-friendly). 422 (weak password) and
        // 400 are surfaced VERBATIM so the invitee sees the exact requirement.
        if (res.status === 401) {
          this.error =
            'This invite link is invalid or has expired — ask an admin to re-send it.';
        } else {
          this.error =
            (payload as { error?: string } | null)?.error ||
            'Something went wrong. Please try again.';
        }
        this.busy = false;
        return;
      }

      const { customToken } = (payload ?? {}) as InviteAcceptResponse;
      if (customToken) {
        await this.activateAndRedirect(customToken);
        return;
      }
      this.error = 'Your account could not be set up. Please try again.';
      this.busy = false;
    } catch {
      this.error = 'Something went wrong. Please try again.';
      this.busy = false;
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  render(): TemplateResult {
    return html`
      <canvas class="ink" aria-hidden="true"></canvas>
      <div class="panel">
        <p class="eyebrow">Workplace by Ricoh</p>
        ${this.token ? this.renderForm() : this.renderInvalid()}
      </div>
    `;
  }

  /** No token on the link — there is nothing to submit; explain and stop. */
  private renderInvalid(): TemplateResult {
    return html`
      <h1>Invalid invite link</h1>
      <p class="subtitle">
        This invite link is missing its token, or has already been used. Ask an
        admin to re-send your invitation.
      </p>
    `;
  }

  /** The set-password form (the common case for a freshly-clicked magic link). */
  private renderForm(): TemplateResult {
    return html`
      <h1>Set your password</h1>
      <p class="subtitle">Finish setting up your Workplace by Ricoh account.</p>
      <div aria-live="polite" role="status">
        ${this.error ? html`<p class="error">${this.error}</p>` : ''}
      </div>
      ${this.done
        ? html`<p class="notice">Signing you in…</p>`
        : html`
            <form @submit=${this.onSubmit} novalidate>
              <div class="field">
                <label for="accept-password">Choose a password</label>
                <input
                  id="accept-password"
                  data-autofocus
                  type="password"
                  name="new-password"
                  autocomplete="new-password"
                  placeholder="Create a password"
                  .value=${this.password}
                  ?disabled=${this.busy}
                  required
                  @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
                />
              </div>
              <div class="field">
                <label for="accept-name">
                  Your name <span style="font-weight:400">(optional)</span>
                </label>
                <input
                  id="accept-name"
                  type="text"
                  name="name"
                  autocomplete="name"
                  placeholder="Your name"
                  .value=${this.name}
                  ?disabled=${this.busy}
                  @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
                />
              </div>
              <button class="primary" type="submit" ?disabled=${this.busy || !this.password}>
                ${this.busy ? 'Setting up…' : 'Set password & sign in'}
              </button>
            </form>
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'accept-page': AcceptPage;
  }
}
